import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Settings, Save, Calculator, Percent, Building2, Receipt } from "lucide-react";

// Admin knobs for the fully-loaded margin on /unified-projects (the "Net" column
// and its breakdown). All percents are whole numbers (5 = 5%).
const LoadedCostSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    payroll_uplift_percent: 5,
    revenue_tax_fop_percent: 5,
    revenue_tax_llc_percent: 20,
    other_expenses_monthly: 0,
    dev_cost_to_software_percent: 100,
    indirect_cap_percent: 100,
    note: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/settings/loaded-cost");
        setSettings({
          payroll_uplift_percent: Number(res.data.payroll_uplift_percent ?? 5),
          revenue_tax_fop_percent: Number(res.data.revenue_tax_fop_percent ?? 5),
          revenue_tax_llc_percent: Number(res.data.revenue_tax_llc_percent ?? 20),
          other_expenses_monthly: Number(res.data.other_expenses_monthly ?? 0),
          dev_cost_to_software_percent: Number(res.data.dev_cost_to_software_percent ?? 100),
          indirect_cap_percent: Number(res.data.indirect_cap_percent ?? 100),
          note: res.data.note || "",
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/loaded-cost", settings);
      toast.success("Loaded-cost settings saved — Net margins recompute within a minute");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const num = (k) => (e) => setSettings({ ...settings, [k]: parseFloat(e.target.value) || 0 });

  const Row = ({ icon, label, desc, children }) => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
      <div className="md:col-span-1">
        <Label className="text-zinc-300 flex items-center gap-2">{icon} {label}</Label>
        <p className="text-xs text-zinc-500 mt-1">{desc}</p>
      </div>
      <div className="md:col-span-2 space-y-2">{children}</div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="loaded-cost-settings-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
            <Calculator className="h-7 w-7 text-emerald-500" />
            Fully-loaded margin
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Drives the <span className="font-mono text-zinc-300">Net</span> column &amp; cost breakdown on{" "}
            <span className="font-mono text-zinc-300">/unified-projects</span>. PM/sales salaries, sales commission
            and PM bonus are pulled from staff records automatically — these knobs add taxes &amp; other overhead.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
            <Settings className="h-4 w-4 text-orange-500" /> Taxes &amp; other costs
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <Row
            icon={<Percent className="h-3.5 w-3.5 text-red-400" />}
            label="Payroll tax uplift"
            desc="% the company pays ON TOP of every salary (ЄСВ etc.). Applied to production, PM, sales and overhead salaries — not to freelance artist/AD fees."
          >
            <div className="flex items-center gap-2 max-w-[200px]">
              <Input type="number" step="0.5" min="0" value={settings.payroll_uplift_percent}
                onChange={num("payroll_uplift_percent")} className="bg-zinc-950 border-zinc-800 text-zinc-50" />
              <span className="text-zinc-500 text-sm">%</span>
            </div>
          </Row>

          <div className="border-t border-zinc-800" />

          <Row
            icon={<Building2 className="h-3.5 w-3.5 text-sky-400" />}
            label="Revenue tax — FOP"
            desc="Tax % on revenue billed through a FOP legal entity (company_type = fop). Applied per payment line."
          >
            <div className="flex items-center gap-2 max-w-[200px]">
              <Input type="number" step="0.5" min="0" value={settings.revenue_tax_fop_percent}
                onChange={num("revenue_tax_fop_percent")} className="bg-zinc-950 border-zinc-800 text-zinc-50" />
              <span className="text-zinc-500 text-sm">%</span>
            </div>
          </Row>

          <div className="border-t border-zinc-800" />

          <Row
            icon={<Building2 className="h-3.5 w-3.5 text-violet-400" />}
            label="Revenue tax — LLC"
            desc="Tax % on LLC/corp-billed revenue. A payment counts as LLC if its legal entity is LLC/corp, it was paid via Stripe (settles to the US LLC), or the project is TRP. Everything else uses the FOP rate."
          >
            <div className="flex items-center gap-2 max-w-[200px]">
              <Input type="number" step="0.5" min="0" value={settings.revenue_tax_llc_percent}
                onChange={num("revenue_tax_llc_percent")} className="bg-zinc-950 border-zinc-800 text-zinc-50" />
              <span className="text-zinc-500 text-sm">%</span>
            </div>
          </Row>

          <div className="border-t border-zinc-800" />

          <Row
            icon={<Receipt className="h-3.5 w-3.5 text-stone-400" />}
            label="Other expenses / month"
            desc="Monthly non-payroll overhead in USD (office rent, software, bank fees, …). Absorbed across active projects by revenue share, the same way staff overhead is. Leave 0 to skip."
          >
            <div className="flex items-center gap-2 max-w-[240px]">
              <span className="text-zinc-500 text-sm">$</span>
              <Input type="number" step="100" min="0" value={settings.other_expenses_monthly}
                onChange={num("other_expenses_monthly")} className="bg-zinc-950 border-zinc-800 text-zinc-50" />
              <span className="text-zinc-500 text-sm">/mo</span>
            </div>
          </Row>

          <div className="border-t border-zinc-800" />

          <Row
            icon={<Percent className="h-3.5 w-3.5 text-teal-400" />}
            label="Dev cost → Spinhouse"
            desc="% of the developer team's payroll that counts as the cost of building Spinhouse/software products. Charged onto software projects only (Spinhouse/DevBase), spread by revenue. Lower it if part of the dev team works on the internal portal/other, not Spinhouse."
          >
            <div className="flex items-center gap-2 max-w-[200px]">
              <Input type="number" step="5" min="0" max="100" value={settings.dev_cost_to_software_percent}
                onChange={num("dev_cost_to_software_percent")} className="bg-zinc-950 border-zinc-800 text-zinc-50" />
              <span className="text-zinc-500 text-sm">%</span>
            </div>
          </Row>

          <div className="border-t border-zinc-800" />

          <Row
            icon={<Percent className="h-3.5 w-3.5 text-amber-400" />}
            label="Indirect cap (% of revenue)"
            desc="Max allocated indirect cost as % of a project's revenue. Allocation spreads fixed salaries by calendar time, so a tiny project left open for months (mostly waiting) can absorb absurd amounts. The excess above this cap is treated as idle/waiting — a company period-cost, not charged to the project. 100 = indirect can't exceed revenue; 0 = no cap."
          >
            <div className="flex items-center gap-2 max-w-[200px]">
              <Input type="number" step="10" min="0" value={settings.indirect_cap_percent}
                onChange={num("indirect_cap_percent")} className="bg-zinc-950 border-zinc-800 text-zinc-50" />
              <span className="text-zinc-500 text-sm">%</span>
            </div>
          </Row>

          <div className="border-t border-zinc-800" />

          <Row icon={null} label="Manager note" desc="Optional context for whoever tunes these numbers.">
            <Textarea rows={3} value={settings.note}
              onChange={(e) => setSettings({ ...settings, note: e.target.value })}
              placeholder="e.g. FOP 5% / LLC 20% per 2025 contracts; other-expenses = rent + Adobe + WS + bank."
              className="bg-zinc-950 border-zinc-800 text-zinc-50" />
          </Row>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/30 border-zinc-800/60">
        <CardContent className="p-4 text-xs text-zinc-500 space-y-1">
          <p><span className="text-zinc-400 font-semibold">How the Net margin is built:</span> Revenue − direct costs
            (freelance artist + Worksection production + AD fee) − PM/sales base salary (capacity-allocated) − sales
            commission − PM bonus − company overhead − payroll tax − revenue tax − other expenses.</p>
          <p>Developers are excluded as a separate cost center. Allocation is an estimate (capacity-based, not
            time-tracked). Changes here recompute within ~1 minute.</p>
          <p><span className="text-zinc-400 font-semibold">Sales commission</span> is skipped for partner-sold deals
            (sales manager flagged as partner) and TRP projects — those carry no commission.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoadedCostSettingsPage;
